/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import {
  Box, Chip, Typography, Collapse,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UpdateStatusEnum, UpdateTypeEnum } from '../../../consts/enums';
import STRING_KEYS from '../../../language/keys';
import Task from '../../../components/Task';
import { useGetUpdates, useReadUpdates } from '../../../hooks/query/useUpdates';
import useNavigate from '../../../hooks/useNavigate';
import style from './style';
import EmptyTasks from './EmptyTasks';
import TasksTitle from './TaskTitle';

type TaskType = {
  id: string,
  key: string,
  title: JSX.Element | string,
  checked: boolean,
  date: Date,
  type: string
}

export default function Tasks(): JSX.Element {
  const [isTasksAggregated, setIsTasksAggregated] = useState(false);
  const [tasks, setTasks] = useState<TaskType[]>([] as TaskType[]);
  const { t: translate } = useTranslation();
  const { goToPositionDetails, goToMatchEdit } = useNavigate();

  const {
    aggregatedTotal, aggregatedUpdates, status, setStatus, loadUpdates, isFetched, refetch,
  } = useGetUpdates();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { mutate: readUpdates } = useReadUpdates(refetch);

  useEffect(() => {
    setIsTasksAggregated(false);
  }, [status]);

  useEffect(() => {
    const keys = Object.keys(aggregatedUpdates);
    if (!isTasksAggregated && keys.length) {
      const t = keys.reduce((accumulator, key) => {
        if (aggregatedUpdates[key].status === UpdateStatusEnum.Read) {
          accumulator.read.push({
            id: aggregatedUpdates[key].id[0],
            title: TasksTitle(aggregatedUpdates[key]),
            checked: aggregatedUpdates[key].status === UpdateStatusEnum.Read,
            key,
            date: aggregatedUpdates[key].createdAt,
            type: aggregatedUpdates[key].type,
          });
        } else {
          accumulator.unread.push({
            id: aggregatedUpdates[key].id[0],
            title: TasksTitle(aggregatedUpdates[key]),
            checked: aggregatedUpdates[key].status === UpdateStatusEnum.Read,
            key,
            date: aggregatedUpdates[key].createdAt,
            type: aggregatedUpdates[key].type,
          });
        }

        return accumulator;
      }, { unread: [] as TaskType[], read: [] as TaskType[] });

      setTasks([...t.unread, ...t.read]);
      setIsTasksAggregated(true);
    }
  }, [loadUpdates]);

  const onCheckedTask = (key: string) => {
    const updatedTasks = tasks.map((task: TaskType) => {
      if (key === task.key) {
        return { ...task, checked: true };
      }

      return task;
    });
    setTasks(updatedTasks);
    readUpdates(aggregatedUpdates[key].id);
  };

  const onClickGoTo = (type: string) => (id: string) => {
    const {
      aggAmount, positionId, accountId, matchId,
    } = aggregatedUpdates[id];

    if (aggAmount > 1 || type === UpdateTypeEnum.OnboardingCompleted) {
      goToPositionDetails(positionId);
    } else if (type === UpdateTypeEnum.matchFlowFailed) {
      goToPositionDetails(positionId, 'runs');
    } else {
      goToMatchEdit(positionId, accountId, matchId);
    }
  };

  const setAll = () => setStatus(UpdateStatusEnum.all);

  const setOpen = () => setStatus(UpdateStatusEnum.Unread);

  const renderTasks = (): JSX.Element | JSX.Element[] => {
    if (!tasks.length && isFetched) {
      return <EmptyTasks />;
    }

    return (
      tasks.map(((task) => {
        return (
          <Collapse
            key={ task.id }
            in={ status === UpdateStatusEnum.all ? true : !task.checked }
            timeout={ 1000 }
          >
            <Box
              sx={ { mt: 3 } }
            >
              <Task
                id={ task.key }
                checked={ task.checked }
                title={ task.title }
                onCheckedTask={ onCheckedTask }
                onClickGoTo={ onClickGoTo(task.type) }
                date={ task.date }
              />
            </Box>
          </Collapse>
        );
      }))
    );
  };

  return (
    <>
      <Box sx={ style.title.main }>
        <Box sx={ style.title.box }>
          <Typography variant={ style.title.variant }>
            { translate(STRING_KEYS.HOME_PAGE.TASKS.TITLE) }
          </Typography>
          <Chip
            label={ aggregatedTotal }
            sx={ style.title.chip }
          />
        </Box>
        <Box sx={ style.title.box }>
          <Typography
            variant={ style.title.filter.variant }
            style={ {
              fontWeight: status === UpdateStatusEnum.Unread ? 'bold' : 'normal',
            } }
            sx={ style.title.filter.sx }
            onClick={ setOpen }
          >
            { translate(STRING_KEYS.HOME_PAGE.TASKS.FILTER_OPEN) }
          </Typography>
          <Typography
            variant={ style.title.filter.variant }
            style={ {
              fontWeight: status === UpdateStatusEnum.all ? 'bold' : 'normal',
              marginLeft: 5,
            } }
            sx={ style.title.filter.sx }
            onClick={ setAll }
          >
            { translate(STRING_KEYS.HOME_PAGE.TASKS.FILTER_ALL) }
          </Typography>
        </Box>
      </Box>
      { renderTasks() }
    </>
  );
}

Tasks.defaultProps = {
  count: 0,
};
